/**
 * Menuraq Template Node Utilities
 * Repeater collection resolution, node-tree helpers, and node-id utilities.
 * Extracted from util.ts; behavior is identical.
 */

import type { MenuData, Template, TemplateNode } from "../types/template";
import { resolvePath, isBindingMap, resolveBindingExpression } from "./templateBindings";

export function normalizeLookupKey(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function isTruthyFeaturedFlag(value: unknown): boolean {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = normalizeLookupKey(value);
    return ["true", "1", "yes", "on", "featured"].includes(normalized);
  }
  return false;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => String(entry ?? "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

function pickCategorySectionId(category: Record<string, unknown> | null | undefined): string | undefined {
  return (
    String(category?.section_id ?? '') ||
    String(category?.sectionId ?? '') ||
    String(category?.section ?? '') ||
    String(category?.group_id ?? '') ||
    String(category?.groupId ?? '') ||
    String((category?.metadata as Record<string, unknown>)?.section_id ?? '') ||
    String((category?.metadata as Record<string, unknown>)?.sectionId ?? '') ||
    undefined
  );
}

export function isFeaturedItem(item: unknown): boolean {
  if (!item) return false;
  const obj = item as Record<string, unknown>;
  const directFlags = [
    obj.is_featured,
    obj.featured,
    obj.isFeatured,
    obj.featured_item,
    obj.isFeaturedItem,
    (obj.metadata as Record<string, unknown> | undefined)?.is_featured,
  ];
  if (directFlags.some((flag) => isTruthyFeaturedFlag(flag))) return true;
  if (Array.isArray(obj.tags)) {
    return (obj.tags as unknown[]).some((tag: unknown) => normalizeLookupKey(tag) === "featured");
  }
  return false;
}

function flattenAllMenuItems(context: Record<string, unknown> | null | undefined): unknown[] {
  if (!context) return [];
  const menu = context.menu as Record<string, unknown> | undefined;
  if (Array.isArray(menu?.items)) return menu.items;
  const categories = Array.isArray(menu?.categories) ? menu.categories : [];
  return (categories as Record<string, unknown>[]).flatMap((category) => (Array.isArray(category?.items) ? category.items : []));
}

function resolveRepeaterSectionId(node: TemplateNode, context: Record<string, unknown>): string | undefined {
  const staticSectionId = node.props?.sectionId;
  if (typeof staticSectionId === "string" && staticSectionId.trim()) {
    return staticSectionId.trim();
  }

  const sectionBinding = node.props?.sectionIdBinding || node.props?.bindings?.sectionId;
  if (typeof sectionBinding === "string" && sectionBinding.trim()) {
    const resolved = resolveBindingExpression(sectionBinding, context);
    if (resolved && resolved.trim()) return resolved.trim();
  }

  if ((context?.section as Record<string, unknown>)?.id) return String((context.section as Record<string, unknown>).id);
  return undefined;
}

function applyRepeaterFilters(node: TemplateNode, repeatFor: string, collection: unknown[], context: Record<string, unknown>): unknown[] {
  let filtered = Array.isArray(collection) ? collection : [];

  // Skip filtering for page-scoped repeaters , the paged-flip engine already
  // pre-assigns the correct categories/items to each page.
  if (repeatFor.startsWith("page.")) {
    return filtered;
  }

  const categoryRepeatLike =
    repeatFor.includes("categories") || repeatFor === "menu.sections" || repeatFor === "section.categories";
  const itemRepeatLike =
    repeatFor.includes("items") || repeatFor === "featuredItems" || repeatFor === "menu.featuredItems";

  if (categoryRepeatLike) {
    const sectionId = resolveRepeaterSectionId(node, context);
    if (sectionId) {
      const target = normalizeLookupKey(sectionId);
      filtered = filtered.filter((category: unknown) => normalizeLookupKey(pickCategorySectionId(category as Record<string, unknown>)) === target);
    }

    const staticCategoryIds = Array.isArray(node.props?.categoryIds) ? node.props?.categoryIds : [];
    const boundCategoryIds = toStringArray(
      typeof node.props?.categoryIdsBinding === "string"
        ? resolveBindingExpression(node.props.categoryIdsBinding, context)
        : undefined,
    );
    const categoryIds = [...staticCategoryIds, ...boundCategoryIds].map((entry) => normalizeLookupKey(entry));
    if (categoryIds.length > 0) {
      const allowed = new Set(categoryIds);
      filtered = filtered.filter((category: unknown) => allowed.has(normalizeLookupKey((category as Record<string, unknown>)?.id as string)));
    }
  }

  if (itemRepeatLike && node.props?.featuredOnly) {
    filtered = filtered.filter((item: unknown) => isFeaturedItem(item));
  }

  return filtered;
}

export function resolveRepeaterCollection(node: TemplateNode, context: Record<string, unknown>): unknown[] {
  const rawRepeatFor = node.props?.bindings?.repeatFor;
  if (typeof rawRepeatFor !== "string" || !rawRepeatFor.trim()) return [];

  const resolvedRepeatFor = resolveBindingExpression(rawRepeatFor, context).trim() || rawRepeatFor.trim();
  let collection: unknown[] = [];

  const c = context as Record<string, any>;
  const scopedCollection = resolveScopedRepeaterCollection(resolvedRepeatFor, context);
  if (Array.isArray(scopedCollection)) {
    collection = scopedCollection;
  } else if (resolvedRepeatFor === "menu.collections" || resolvedRepeatFor === "collections") {
    collection = c.menu?.collections || [];
  } else if (resolvedRepeatFor === "menu.categories" || resolvedRepeatFor === "categories") {
    collection = c.menu?.categories || [];
  } else if (resolvedRepeatFor === "menu.sections" || resolvedRepeatFor === "sections") {
    collection = c.menu?.sections || [];
  } else if (resolvedRepeatFor === "collection.categories") {
    collection = c.collection?.categories || [];
  } else if (resolvedRepeatFor === "collection.sections") {
    collection = c.collection?.sections || [];
  } else if (resolvedRepeatFor === "section.categories") {
    collection = c.section?.categories || [];
  } else if (resolvedRepeatFor === "items") {
    collection =
      c.category?.items ||
      c.page?.items ||
      c.collection?.items ||
      flattenAllMenuItems(context);
  } else if (resolvedRepeatFor === "collection.items") {
    collection = c.collection?.items || [];
  } else if (resolvedRepeatFor === "featuredItems" || resolvedRepeatFor === "category.featuredItems") {
    collection =
      context?.featuredItems ||
      c.category?.featured_items ||
      (c.category?.items || []).filter((item: unknown) => isFeaturedItem(item));
  } else if (resolvedRepeatFor === "collection.featuredItems") {
    const base = Array.isArray(c.collection?.featuredItems)
      ? c.collection.featuredItems
      : Array.isArray(c.collection?.items)
        ? c.collection.items.filter((item: unknown) => isFeaturedItem(item))
        : [];
    collection = base;
  } else if (resolvedRepeatFor === "menu.items") {
    collection = flattenAllMenuItems(context);
  } else if (resolvedRepeatFor === "menu.featuredItems") {
    const base = Array.isArray(c.menu?.featuredItems) ? c.menu.featuredItems : flattenAllMenuItems(context);
    collection = base.filter((item: unknown) => isFeaturedItem(item));
  } else if (resolvedRepeatFor === "menu.categoriesBySection" || resolvedRepeatFor === "collection.categoriesBySection") {
    const sectionId = resolveRepeaterSectionId(node, context);
    const categoriesBySection = resolvedRepeatFor.startsWith("collection.")
      ? c.collection?.categoriesBySection || {}
      : c.menu?.categoriesBySection || {};
    if (sectionId) {
      collection = categoriesBySection[sectionId] || categoriesBySection[normalizeLookupKey(sectionId)] || [];
    } else {
      const all = Object.values(categoriesBySection).flatMap((value) => (Array.isArray(value) ? value : []));
      const seen = new Set<string>();
      collection = all.filter((category: Record<string, unknown>) => {
        const key = String(category?.id || "");
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
  } else if (resolvedRepeatFor.startsWith("menu.categoriesBySection.")) {
    const sectionId = resolvedRepeatFor.replace("menu.categoriesBySection.", "");
    const categoriesBySection = c.menu?.categoriesBySection || {};
    collection = categoriesBySection[sectionId] || categoriesBySection[normalizeLookupKey(sectionId)] || [];
  } else if (resolvedRepeatFor.startsWith("collection.categoriesBySection.")) {
    const sectionId = resolvedRepeatFor.replace("collection.categoriesBySection.", "");
    const categoriesBySection = c.collection?.categoriesBySection || {};
    collection = categoriesBySection[sectionId] || categoriesBySection[normalizeLookupKey(sectionId)] || [];
  } else {
    const dynamic = resolvePath(context, resolvedRepeatFor);
    if (Array.isArray(dynamic)) collection = dynamic;
  }

  return applyRepeaterFilters(node, resolvedRepeatFor, collection, context);
}

export type RepeaterContextKey =
  | "category"
  | "collection"
  | "item"
  | "promotion"
  | "section"
  | "featuredItem"
  | "page"
  | "metaRow"
  | "tocEntry"
  | "value";

function collectScopedPageItems(pageLike: Record<string, unknown> | null | undefined): unknown[] {
  if (Array.isArray(pageLike?.items)) {
    return pageLike.items;
  }

  if (!Array.isArray(pageLike?.categories)) {
    return [];
  }

  return pageLike.categories.flatMap((category: Record<string, unknown>) =>
    Array.isArray(category?.items) ? category.items : [],
  );
}

function resolveScopedRepeaterCollection(repeatFor: string, context: Record<string, unknown>): unknown[] | undefined {
  const c = context as Record<string, any>;
  if (repeatFor === "collections") {
    if (Array.isArray(context?.collections)) return context.collections;
    if (Array.isArray(c.menu?.collections)) return c.menu.collections;
    return undefined;
  }

  if (repeatFor === "categories") {
    if (Array.isArray(context?.categories)) return context.categories;
    return undefined;
  }

  if (repeatFor === "sections") {
    if (Array.isArray(context?.sections)) return context.sections;
    return undefined;
  }

  if (repeatFor === "items") {
    if (Array.isArray(context?.items)) return context.items;
    if (Array.isArray(c.category?.items)) return c.category.items;
    if (Array.isArray(c.page?.items)) return c.page.items;
    return undefined;
  }

  if (repeatFor === "featuredItems") {
    if (Array.isArray(context?.featuredItems)) return context.featuredItems;
    return undefined;
  }

  if (repeatFor === "metaRows") {
    if (Array.isArray(context?.metaRows)) return context.metaRows;
    if (Array.isArray(c.page?.metaRows)) return c.page.metaRows;
    return undefined;
  }

  if (repeatFor === "tocEntries") {
    if (Array.isArray(context?.tocEntries)) return context.tocEntries;
    if (Array.isArray(c.page?.tocEntries)) return c.page.tocEntries;
    return undefined;
  }

  if (repeatFor === "pages") {
    if (Array.isArray(context?.pages)) return context.pages;
    if (Array.isArray(c.book?.pages)) return c.book.pages;
    return undefined;
  }

  if (repeatFor === "chapters") {
    if (Array.isArray(context?.chapters)) return context.chapters;
    if (Array.isArray(c.book?.chapters)) return c.book.chapters;
    return undefined;
  }

  return undefined;
}

function buildScopedRepeaterAliases(
  contextKey: RepeaterContextKey,
  entry: unknown,
): Record<string, unknown> {
  const e = entry as Record<string, unknown> | null | undefined;
  if (contextKey === "category") {
    return {
      items: Array.isArray(e?.items) ? e.items : [],
    };
  }

  if (contextKey === "collection") {
    return {
      categories: Array.isArray(e?.categories) ? e.categories : [],
      items: Array.isArray(e?.items) ? e.items : [],
      sections: Array.isArray(e?.sections) ? e.sections : [],
      featuredItems: Array.isArray(e?.featuredItems) ? e.featuredItems : [],
    };
  }

  if (contextKey === "section") {
    return {
      categories: Array.isArray(e?.categories) ? e.categories : [],
      featuredItems: Array.isArray(e?.featuredItems) ? e.featuredItems : [],
    };
  }

  if (contextKey === "page") {
    return {
      categories: Array.isArray(e?.categories) ? e.categories : [],
      items: collectScopedPageItems(e),
      metaRows: Array.isArray(e?.metaRows) ? e.metaRows : [],
      tocEntries: Array.isArray(e?.tocEntries) ? e.tocEntries : [],
    };
  }

  return {};
}

export function inferRepeaterContextKey(repeatForRaw: string | undefined): RepeaterContextKey {
  const repeatFor = String(repeatForRaw || "").trim();
  if (!repeatFor) return "value";

  if (repeatFor === "menu.collections") return "collection";
  if (repeatFor === "collection.sections") return "section";
  if (repeatFor === "collection.categories") return "category";
  if (repeatFor === "collection.items" || repeatFor === "collection.featuredItems") return "item";
  if (repeatFor === "section.categories") return "category";
  if (
    repeatFor === "menu.categoriesBySection" ||
    repeatFor.startsWith("menu.categoriesBySection.") ||
    repeatFor === "collection.categoriesBySection" ||
    repeatFor.startsWith("collection.categoriesBySection.")
  ) return "category";
  if (repeatFor === "menu.sections") return "section";
  if (repeatFor === "menu.featuredItems") return "item";
  if (repeatFor === "menu.promotions" || repeatFor.endsWith(".promotions")) return "promotion";
  if (repeatFor === "page.metaRows" || repeatFor.endsWith(".metaRows")) return "metaRow";
  if (repeatFor === "page.tocEntries" || repeatFor.endsWith(".tocEntries")) return "tocEntry";
  if (repeatFor === "pages" || repeatFor === "book.pages" || repeatFor.endsWith(".pages")) return "page";

  if (repeatFor.includes("collections")) return "collection";
  if (repeatFor.includes("categories")) return "category";
  if (repeatFor.includes("items")) return "item";
  if (repeatFor.includes("promotion")) return "promotion";
  if (repeatFor.includes("section")) return "section";
  if (repeatFor.includes("metaRows")) return "metaRow";
  if (repeatFor.includes("tocEntries")) return "tocEntry";
  if (repeatFor.includes("pages")) return "page";
  if (repeatFor.includes("featuredItems")) return "item";
  return "value";
}

export function extendRepeaterContext(
  context: Record<string, unknown>,
  contextKey: RepeaterContextKey,
  entry: unknown,
): Record<string, unknown> {
  const nextContext: Record<string, unknown> = {
    ...context,
    value: entry,
    ...buildScopedRepeaterAliases(contextKey, entry),
  };

  if (contextKey !== "value") {
    nextContext[contextKey] = entry;
  }

  return nextContext;
}

export function resolveActivePromotion(context: Record<string, unknown>) {
  const c = context as Record<string, any>;
  return (
    context?.promotion ||
    context?.promo ||
    c.item?.promotion ||
    c.category?.promotion ||
    null
  );
}

/**
 * Utility to chunk an array into N-sized pages (for sliders/pagination).
 */
export function paginate<T>(arr: T[], itemsPerPage: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < arr.length; i += itemsPerPage) {
    pages.push(arr.slice(i, i + itemsPerPage));
  }
  return pages;
}

/**
 * Utility to split an array into two columns (for splitColumns templates).
 */
export function splitColumns<T>(arr: T[]): [T[], T[]] {
  const col1: T[] = [];
  const col2: T[] = [];
  arr.forEach((item, idx) => {
    (idx % 2 === 0 ? col1 : col2).push(item);
  });
  return [col1, col2];
}

/**
 * Find a node by id in a template node tree.
 */
export function findNodeById(node: TemplateNode, id: string): TemplateNode | null {
  if (node.id === id) return node;
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
  }
  return null;
}

export function findNodesByProp(
  node: TemplateNode,
  prop: string,
  value: unknown,
  acc: TemplateNode[] = []
): TemplateNode[] {
  if (node.props && (node.props as Record<string, unknown>)[prop] === value) acc.push(node);
  if (node.children) {
    node.children.forEach(child => findNodesByProp(child, prop, value, acc));
  }
  return acc;
}


/**
 * Deterministically pick the first available image from a category/item array.
 * Random image selection was removed to keep template rendering predictable.
 */
export function getRandomImage(arr: { image?: string }[]): string | undefined {
  const images = arr.map(x => x.image).filter(Boolean);
  if (images.length === 0) return undefined;
  return images[0];
}

/**
 * Calculate items per page for a slider, based on container width and item minWidth.
 * Ensures at least 1 item per page.
 */
export function getItemsPerPage(containerWidth: number, itemMinWidth: number): number {
  return Math.max(1, Math.floor(containerWidth / itemMinWidth));
}


export function generateNodeId(
  node: TemplateNode,
  idx: number | null = null,
  path = ""
): string {
  // If already unique, return as-is
  if (node && typeof node.id === "string" && node.id.includes("__")) {
    return node.id;
  }
  let base = "";
  if (typeof idx === "number") base += `${idx}__`;
  if (node && typeof node.id === "string") {
    base += node.id;
  } else if (node && node.type) {
    base += node.type;
  } else {
    base += "node";
  }
  // The suffix must be deterministic: the server and the client render the same
  // tree, so a random suffix produced a different DOM id on each side and broke
  // hydration on every public menu load. The node's position path is unique
  // within the tree, which is the uniqueness this needs.
  return path ? `${base}_${path}` : base;
}


// --- Inject bindings for repeaters ---
export function injectBindings(
  node: TemplateNode,
  data: Record<string, unknown>,
  type: "category" | "collection" | "item" | "section" | "page" | "metaRow" | "tocEntry",
  idx: number | null = null,
): TemplateNode {
  const replaceRepeaterTokens = (value: unknown): unknown => {
    if (typeof value === "string") {
      return value.replace(
        new RegExp(`\\{${type}\\.(.+?)\\}`, "g"),
        (_: unknown, prop: string) => {
          const resolved = resolvePath(data, prop) ?? data[prop];
          return resolved != null ? String(resolved) : "";
        }
      );
    }

    if (Array.isArray(value)) {
      return value.map((entry) => replaceRepeaterTokens(entry));
    }

    if (isBindingMap(value)) {
      const output: Record<string, unknown> = {};
      for (const [key, entry] of Object.entries(value)) {
        output[key] = replaceRepeaterTokens(entry);
      }
      return output;
    }

    return value;
  };

  // Deep clone node to avoid mutating the template
  const cloned = JSON.parse(JSON.stringify(node));
  function walk(n: Record<string, unknown>, idx: number | null, path = "r") {
    const isUnique = isUniqueNodeId(n.id as string);
    if (!isUnique) {
      n.id = generateNodeId(n as unknown as TemplateNode, idx, path)
    }

    if (isBindingMap(n.props)) {
      const nextProps: Record<string, unknown> = { ...n.props as Record<string, unknown> };
      for (const [key, value] of Object.entries(nextProps)) {
        if (key === "bindings" && !isBindingMap(value)) continue;
        nextProps[key] = replaceRepeaterTokens(value);
      }
      n.props = nextProps;
    }

    if (n.children) {
      (n.children as Record<string, unknown>[]).forEach((child, childIdx) =>
        walk(child, childIdx, `${path}-${childIdx}`),
      );
    }
  }
  // Seed the path with the repeat index so two structurally identical subtrees
  // (e.g. the item rows of category 0 and category 1) don't resolve to the same
  // ids , the tree path alone is only unique *within* one injectBindings pass.
  walk(cloned, idx, typeof idx === "number" ? `r${idx}` : "r");
  return cloned;
}

export function findAllRepeaters(node: TemplateNode): TemplateNode[] {
  let repeaters: TemplateNode[] = [];
  if (node.props?.repeater) repeaters.push(node);
  if (node.children) {
    for (const child of node.children) {
      repeaters = repeaters.concat(findAllRepeaters(child));
    }
  }
  return repeaters;
}

export function formatPrice(
  value: number | string,
  decimals: number = 2
): string {
  const num = typeof value === "string" ? Number(value) : value;

  if (isNaN(num)) return "0.00";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

export function buildNodeIdMap(template: Template, data: MenuData): Record<string, TemplateNode> {
  const map: Record<string, TemplateNode> = {};

  function walk(node: TemplateNode, context: Record<string, unknown>) {
    map[node.id] = node;
    if (node.props?.repeater) {
      const repeatFor = node.props?.bindings?.repeatFor;
      const repeaterData = resolveRepeaterCollection(node, context);
      const contextKey = inferRepeaterContextKey(repeatFor);

      repeaterData.forEach((entry: unknown, idx: number) => {
        (node.children || []).forEach((child) => {
          const repeatedNode = {
            ...child,
            id: `${node.id}__${idx}__${child.id}`,
          };
          const nextContext =
            extendRepeaterContext(context, contextKey, entry);
          walk(repeatedNode, nextContext);
        });
      });
    } else if (node.children) {
      node.children.forEach(child => walk(child, context));
    }
  }

  template.nodes?.forEach(node => walk(node, { ...data, template }));
  return map;
}

export function isUniqueNodeId(id: string): boolean {
  // You can adjust the pattern if your unique IDs have a different structure
  return typeof id === "string" && id.includes("__");
}
